import cn from 'classnames';
import Checkbox from '@creditflowresearch/uilib/Checkbox';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import Flex from '@creditflowresearch/uilib/Flex';
import BadgeButton from '@/creditflowresearch/ui/common/components/molecules/BadgeButton';
import FilterSection from '@/creditflowresearch/ui/common/components/templates/FilterSection';
import FilterSectionHeader from '@/creditflowresearch/ui/common/components/organisms/FilterSectionHeader';
import FilterGroup from '@framework/ui/Organisms/Filters/components/FilterGroup';
import FilterItem, { filterItemStyles } from '@/creditflowresearch/ui/common/components/FilterItem';
import {
  FilterSectionIds,
} from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/constants';
import {
  toggle,
  toggleMany,
} from '@/creditflowresearch/application/BondScreener/selection';
import useIndustriesSubindustriesSection from './useIndustriesSubindustriesSection';

import styles
  from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/Sections/styles/twoLevelFilter.module.scss';

const SEGMENT_TOOLTIP = (
  <span className={ styles.presetButtonTooltip }>
    Bulk select all relevant options from the sub-industry list.
    This selection will not override any options you have already pre-selected.
  </span>
);

const IndustriesSubindustries = () => {
  const { industryItems, selected, updateSelection, segments } = useIndustriesSubindustriesSection();

  const allChildIds = industryItems.flatMap((industry) => industry.allIds);
  const isAllSelected = allChildIds.length > 0 && allChildIds.every((id) => selected.includes(id));

  return (
    <FilterSection
      sectionId={ FilterSectionIds.IndustriesSubindustries }
      header={ (
        <FilterSectionHeader
          title="INDUSTRIES & SUBINDUSTRIES"
          leftSlot={ (
            <Checkbox
              checked={ isAllSelected }
              indeterminate={ !isAllSelected && selected.length > 0 }
              onChange={ () => updateSelection(toggleMany(selected, allChildIds)) }
              supportIndeterminate
              dataTest="industriesFiltersAllCheckbox"
            />
          ) }
        />
      ) }
    >
      <Flex gap={ 10 }>
        { segments.map((segment) => (
          <BadgeButton
            key={ segment.id }
            label={ segment.label }
            value={ segment.id }
            isActive={ segment.isActive }
            onClick={ () => updateSelection(toggleMany(selected, segment.ids)) }
            tooltip={ SEGMENT_TOOLTIP }
            dataTest={ `segment-${ segment.label }` }
            className={ cn(styles.presetButton, { [styles.presetButtonActive]: segment.isActive }) }
            tooltipClassName={ styles.presetButtonTooltipContainer }
            tooltipPlacement={ 'bottom-start' }
          />
        )) }
      </Flex>

      <div className={ styles.separator } />

      { industryItems.map((industry) => (
        <FilterGroup
          key={ industry.id }
          groupId={ industry.id }
          isExpanded={ false }
          showArrow={ industry.children.length > 0 }
          itemsCount={ industry.enabledIds.length }
          selectedItemsCount={ industry.selectedCount }
          checkboxComponent={ (
            <Checkbox
              checked={ industry.allIds.length > 0 && industry.totalSelectedCount === industry.allIds.length }
              indeterminate={ industry.totalSelectedCount > 0 && industry.totalSelectedCount < industry.allIds.length }
              onChange={ () => updateSelection(toggleMany(selected, industry.allIds)) }
              supportIndeterminate
              label={ industry.name }
              dataTest={ `industry-${ industry.id }` }
            />
          ) }
        >
          { industry.children.map((sub) => (
            <FilterItem
              key={ sub.id }
              className={ cn(filterItemStyles.filterItem, filterItemStyles.filterNestedItem) }
              isExpandable={ false }
            >
              <Checkbox
                checked={ selected.includes(sub.id) }
                disabled={ sub.disabled }
                onChange={ () => updateSelection(toggle(selected, sub.id)) }
                label={ sub.disabled ? (
                  <Tooltip content="No matching tranches with current filters">
                    { sub.name }
                  </Tooltip>
                ) : sub.name }
                dataTest={ `subindustry-${ sub.id }` }
              />
            </FilterItem>
          )) }
        </FilterGroup>
      )) }
    </FilterSection>
  );
};

export default IndustriesSubindustries;
